// MF-67 — Location picker for a journal entry: pick a Maestro-managed zone (the stable key for the
// "incidents by area" heatmap) and optionally drop a precise pin on a free Leaflet/OpenStreetMap map
// (no API key). Value shape: { location, zoneId, geo: { lat, lng } }.
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Autocomplete, TextField, Typography, Button } from '@mui/material'
import MapPicker from './MapPicker'
import FormField from './FormField'
import { inputSx } from './formStyles'
import { getZones } from '../../../../services/journal/zones.service'

const LOCATION_HELP =
  'Where it happened. Pick a Maestro-managed zone (Yard, Gym, Bus bay…) — the stable key that powers the "by location" reports; add a map pin for extra precision.'

const LocationField = ({ value = {}, onChange }) => {
  const [zones, setZones] = useState([])
  const schoolGeo = useSelector((state) => state?.tenant?.tenant?.geo)
  const { location = '', zoneId = null, geo = {} } = value

  useEffect(() => {
    getZones({ limit: 500 })
      .then(({ data }) => setZones(data?.paginatedResults ?? []))
      .catch(() => setZones([]))
  }, [])

  const selectedZone = zones.find((z) => String(z._id) === String(zoneId)) || null
  const hasPin = geo?.lat != null && geo?.lng != null

  return (
    <FormField
      label='Location'
      help={LOCATION_HELP}
      hint={zones.length === 0 ? 'No zones yet — add them under Journal · Zones, or type an area.' : ''}
    >
      <Autocomplete
        freeSolo
        size='small'
        options={zones}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o?.name || '')}
        value={selectedZone || location || null}
        onChange={(_, picked) => {
          if (picked && typeof picked === 'object') {
            // A zone was chosen: use its name as the label, keep its id, seed the pin from its coords.
            onChange({
              location: picked.name,
              zoneId: picked._id,
              geo: {
                lat: picked.lat ?? geo.lat ?? null,
                lng: picked.lng ?? geo.lng ?? null
              }
            })
          } else {
            onChange({ ...value, location: picked || '', zoneId: null })
          }
        }}
        onInputChange={(_, text, reason) => {
          if (reason === 'input') onChange({ ...value, location: text, zoneId: null })
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder='Pick a zone or type an area' sx={inputSx} />
        )}
      />

      <div style={{ height: 8 }} />
      <MapPicker
        value={geo}
        center={schoolGeo}
        onChange={(pt) => onChange({ ...value, geo: pt })}
        height={280}
      />
      <div className='flex items-center gap-3'>
        <Typography style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>
          {hasPin
            ? `Pin: ${Number(geo.lat).toFixed(5)}, ${Number(geo.lng).toFixed(5)}`
            : 'Click the map to drop a pin (optional).'}
        </Typography>
        {hasPin ? (
          <Button
            size='small'
            color='inherit'
            onClick={() => onChange({ ...value, geo: { lat: null, lng: null } })}
          >
            Clear pin
          </Button>
        ) : null}
      </div>
    </FormField>
  )
}

export default LocationField
